import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";

export default function CartDrawer() {
  const { items, remove, subtotal, open, setOpen, count } = useCart();
  const nav = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0a0b0e] border-l border-white/10 z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div>
                <div className="kyro-overline">Your Vault</div>
                <h3 className="font-display font-bold text-xl mt-1">{count} item{count === 1 ? "" : "s"}</h3>
              </div>
              <button data-testid="close-cart-btn" onClick={() => setOpen(false)} className="w-9 h-9 grid place-items-center rounded-full border border-white/10 hover:bg-white/5">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {items.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-kyro-mute mb-6">Your vault is empty.</p>
                  <button data-testid="empty-cart-shop-btn" onClick={() => { setOpen(false); nav("/shop"); }} className="kyro-btn-secondary text-sm">Browse subscriptions</button>
                </div>
              )}
              {items.map((it) => (
                <div key={it.key} data-testid={`cart-item-${it.slug}`} className="kyro-card p-4 flex gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/5 shrink-0">
                    {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{it.name}</p>
                        <p className="text-xs text-kyro-mute mt-1">{it.plan_label}</p>
                      </div>
                      <button data-testid={`remove-cart-${it.slug}`} onClick={() => remove(it.key)} className="text-kyro-mute hover:text-[#FF4433]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <p className="mt-2 font-display font-bold">₹{it.price.toFixed(0)}</p>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/5 p-5 space-y-4 bg-[#0b0d11]">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-kyro-mute">Subtotal</span>
                  <span className="font-semibold">₹{subtotal.toFixed(0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-kyro-mute text-sm">Total</span>
                  <span className="font-display text-2xl font-black">₹{subtotal.toFixed(0)}</span>
                </div>
                <button
                  data-testid="proceed-to-checkout-btn"
                  onClick={() => { setOpen(false); nav("/checkout"); }}
                  className="w-full kyro-btn-primary flex items-center justify-center gap-2"
                >
                  Proceed to checkout <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] text-center text-kyro-faint">1–2 hr delivery • Secure payment • 24/7 support</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
